import { Link } from 'react-router-dom'
import { Logo } from '../components/logo'
import { Camera, BarChart2, Search, Sparkles, Mail, TrendingUp, FolderOpen, ShieldCheck } from 'lucide-react'
import { FaInstagram, FaTiktok } from 'react-icons/fa'

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white flex flex-col">

      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Logo />
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-gray-500 hover:text-gray-900">Sign in</Link>
          <Link to="/signup" className="text-sm bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:opacity-90">
            Get started free
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="flex flex-col items-center text-center px-6 pt-14 pb-8">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 max-w-xl leading-tight">
          Snap it. Forward it.<br />Buylog does the rest.
        </h1>
        <p className="mt-4 text-lg text-gray-400 max-w-sm">
          No bank connection. No manual entry.
        </p>
        <Link to="/signup" className="mt-8 bg-primary text-primary-foreground px-8 py-3 rounded-lg font-semibold text-base hover:opacity-90">
          Try it for free
        </Link>
        <p className="mt-3 text-xs text-gray-400">Free · No credit card required</p>
        <a href="https://apps.apple.com/us/app/buylog-smart-shopping-log/id6761249199" target="_blank" rel="noopener noreferrer" className="mt-3">
          <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg" alt="Download on the App Store" className="h-10" />
        </a>
      </section>

      {/* Savings Review callout */}
      <section className="px-6 py-12 bg-amber-50">
        <div className="max-w-2xl mx-auto flex flex-col items-center text-center gap-8">
          <div>
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <span className="text-xs font-mono text-amber-600 uppercase tracking-widest">Savings Review</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">One tap. See what you could be saving.</h2>
            <p className="mt-3 text-sm text-gray-500 max-w-sm mx-auto">
              Buylog looks at your last 6 months of receipts and shows you forgotten subscriptions, duplicate services, and charges that keep going up. It only uses your own data.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-lg">
            <div className="rotate-[-1deg] border-2 border-gray-800 rounded-sm p-4 flex-1 bg-white"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="font-mono text-[10px] text-amber-500 uppercase tracking-wide mb-2">Forgotten charge</div>
              <div className="font-mono text-sm font-bold text-gray-800">Netflix</div>
              <div className="font-mono text-[10px] text-gray-500 mt-1">Still being charged? Netflix has charged you every month for 12 months.</div>
            </div>
            <div className="rotate-[1deg] border-2 border-gray-800 rounded-sm p-4 flex-1 bg-white"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="font-mono text-[10px] text-amber-500 uppercase tracking-wide mb-2">Buy smarter</div>
              <div className="font-mono text-sm font-bold text-gray-800">Coffee</div>
              <div className="font-mono text-[10px] text-gray-500 mt-1">You buy this at Starbucks and Philz Coffee. Philz Coffee is cheaper. You could save $200 a year by switching.</div>
            </div>
          </div>
          <Link to="/signup" className="bg-amber-500 text-white px-8 py-3 rounded-lg font-semibold text-base hover:opacity-90">
            Find my wasted money
          </Link>
        </div>
      </section>

      {/* Doodle demo */}
      <section className="px-6 py-14 flex flex-col items-center gap-10">

        {/* Row 1: Snap a receipt */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1.5 text-gray-600">
            <Camera className="h-4 w-4" />
            <span className="text-xs font-semibold tracking-wide">Snap a receipt</span>
          </div>
          <div className="flex items-center gap-4 sm:gap-8">
            <div className="rotate-[-2deg] border-2 border-gray-800 rounded-sm p-4 w-36 sm:w-44 bg-white font-mono text-xs text-gray-700 space-y-1.5"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="text-center font-bold text-sm border-b border-dashed border-gray-400 pb-1.5 mb-2">EARLS KITCHEN</div>
              <div className="flex justify-between"><span>Pasta</span><span>$22.00</span></div>
              <div className="flex justify-between"><span>Glass of Wine</span><span>$14.00</span></div>
              <div className="flex justify-between"><span>Tiramisu</span><span>$9.00</span></div>
              <div className="border-t border-dashed border-gray-400 pt-1.5 flex justify-between font-bold">
                <span>TOTAL</span><span>$45.00</span>
              </div>
              <div className="text-center text-gray-400 text-[10px] pt-1">Mar 8, 2026</div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <svg width="48" height="24" viewBox="0 0 48 24" fill="none" className="text-primary">
                <path d="M2 12 C10 10, 20 8, 36 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
                <path d="M32 7 L38 12 L32 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span className="text-[10px] font-mono text-gray-400">fills it in</span>
            </div>
            <div className="rotate-[1.5deg] border-2 border-gray-800 rounded-sm p-4 w-36 sm:w-44 bg-white space-y-2"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="font-mono text-[10px] text-gray-400 uppercase tracking-wide">Expense logged</div>
              <div className="font-bold text-sm text-gray-900">Dinner</div>
              <div className="space-y-1 font-mono text-xs text-gray-600">
                <div className="flex justify-between"><span>Merchant</span><span className="text-gray-900">Earls</span></div>
                <div className="flex justify-between"><span>Date</span><span className="text-gray-900">Mar 8</span></div>
                <div className="flex justify-between"><span>Category</span><span className="text-gray-900">Food</span></div>
              </div>
              <div className="border-t border-dashed border-gray-300 pt-2 font-bold text-sm flex justify-between">
                <span className="font-mono text-gray-500">Total</span>
                <span className="text-primary">$45.00</span>
              </div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 w-full max-w-md">
          <div className="flex-1 border-t border-dashed border-gray-200" />
          <span className="text-[10px] font-mono text-gray-300 uppercase tracking-widest">or</span>
          <div className="flex-1 border-t border-dashed border-gray-200" />
        </div>

        {/* Row 2: Forward an email */}
        <div className="flex flex-col items-center gap-2">
          <div className="flex items-center gap-1.5 text-gray-600">
            <Mail className="h-4 w-4" />
            <span className="text-xs font-semibold tracking-wide">Forward an email</span>
          </div>
          <div className="flex items-center gap-4 sm:gap-8">
            <div className="rotate-[-1.5deg] border-2 border-gray-800 rounded-sm p-4 w-36 sm:w-44 bg-white font-mono text-xs text-gray-700 space-y-1.5"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="text-center font-bold text-sm border-b border-dashed border-gray-400 pb-1.5 mb-2">ORDER CONFIRMED</div>
              <div className="text-[10px] text-gray-400 mb-1">From: amazon.ca</div>
              <div className="flex justify-between"><span>USB-C Cable</span><span>$12.99</span></div>
              <div className="flex justify-between"><span>Phone Stand</span><span>$24.99</span></div>
              <div className="border-t border-dashed border-gray-400 pt-1.5 flex justify-between font-bold">
                <span>ORDER TOTAL</span><span>$37.98</span>
              </div>
              <div className="text-center text-gray-400 text-[10px] pt-1">Mar 12, 2026</div>
            </div>
            <div className="flex flex-col items-center gap-1">
              <svg width="48" height="24" viewBox="0 0 48 24" fill="none" className="text-primary">
                <path d="M2 12 C10 10, 20 8, 36 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/>
                <path d="M32 7 L38 12 L32 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
              <span className="text-[10px] font-mono text-gray-400">logs itself</span>
            </div>
            <div className="rotate-[1deg] border-2 border-gray-800 rounded-sm p-4 w-36 sm:w-44 bg-white space-y-2"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              <div className="font-mono text-[10px] text-gray-400 uppercase tracking-wide">Expense logged</div>
              <div className="font-bold text-sm text-gray-900">Amazon</div>
              <div className="space-y-1 font-mono text-xs text-gray-600">
                <div className="flex justify-between"><span>Merchant</span><span className="text-gray-900">Amazon</span></div>
                <div className="flex justify-between"><span>Date</span><span className="text-gray-900">Mar 12</span></div>
                <div className="flex justify-between"><span>Category</span><span className="text-gray-900">Shopping</span></div>
              </div>
              <div className="border-t border-dashed border-gray-300 pt-2 font-bold text-sm flex justify-between">
                <span className="font-mono text-gray-500">Total</span>
                <span className="text-primary">$37.98</span>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Doodle — search + insights */}
      <section className="px-6 pb-10 flex justify-center">
        <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-8">

          {/* Search doodle */}
          <div className="flex flex-col gap-2">
            <div className="text-[10px] font-mono text-gray-400 text-center mb-1">search anything</div>
            <div className="rotate-[-1deg] border-2 border-gray-800 rounded-sm p-3 w-52 sm:w-60 bg-white space-y-2"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              {/* Search bar */}
              <div className="flex items-center gap-2 border border-dashed border-gray-400 rounded-sm px-2 py-1">
                <Search className="h-3 w-3 text-gray-400 shrink-0" />
                <span className="font-mono text-xs text-gray-700">prescription</span>
                <span className="animate-pulse font-mono text-xs text-gray-400">|</span>
              </div>
              {/* Results */}
              <div className="space-y-1.5">
                <div className="flex justify-between items-center border border-dashed border-gray-200 rounded-sm px-2 py-1">
                  <div>
                    <p className="font-mono text-xs font-bold text-gray-800">Prescription</p>
                    <p className="font-mono text-[10px] text-gray-400">Shoppers · Mar 12</p>
                  </div>
                  <span className="font-mono text-xs font-bold text-gray-800">$24.50</span>
                </div>
                <div className="flex justify-between items-center border border-dashed border-gray-200 rounded-sm px-2 py-1">
                  <div>
                    <p className="font-mono text-xs font-bold text-gray-800">Rx Refill</p>
                    <p className="font-mono text-[10px] text-gray-400">Rexall · Feb 3</p>
                  </div>
                  <span className="font-mono text-xs font-bold text-gray-800">$18.00</span>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden sm:block text-gray-200 text-2xl font-thin">|</div>

          {/* Trends doodle */}
          <div className="flex flex-col gap-2">
            <div className="text-[10px] font-mono text-gray-400 text-center mb-1">spending over time</div>
            <div className="rotate-[-0.5deg] border-2 border-gray-800 rounded-sm p-3 w-52 sm:w-60 bg-white"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              {/* Chart */}
              <svg viewBox="0 0 200 80" className="w-full" style={{ fontFamily: 'monospace' }}>
                {/* Grid lines */}
                {[20, 40, 60].map(y => (
                  <line key={y} x1="10" y1={y} x2="195" y2={y} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="3,3" />
                ))}
                {/* Budget line */}
                <line x1="10" y1="25" x2="195" y2="25" stroke="#eab308" strokeWidth="1.5" strokeDasharray="4,3" />
                <text x="130" y="21" fontSize="7" fill="#eab308" fontFamily="monospace">budget $800</text>
                {/* Spending line — curved upward */}
                <polyline
                  points="10,72 40,65 70,55 100,48 130,38 160,32 190,28"
                  fill="none"
                  stroke="#1f2937"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Area fill */}
                <polygon
                  points="10,72 40,65 70,55 100,48 130,38 160,32 190,28 190,78 10,78"
                  fill="#eab308"
                  fillOpacity="0.08"
                />
                {/* Dots */}
                {[[10,72],[40,65],[70,55],[100,48],[130,38],[160,32],[190,28]].map(([x,y], i) => (
                  <circle key={i} cx={x} cy={y} r="2.5" fill="#1f2937" />
                ))}
                {/* X labels */}
                {['1','5','10','15','20','25','28'].map((label, i) => (
                  <text key={label} x={10 + i * 30} y="78" fontSize="6" fill="#9ca3af" textAnchor="middle" fontFamily="monospace">{label}</text>
                ))}
              </svg>
              <div className="flex justify-between font-mono text-[10px] text-gray-400 mt-1 border-t border-dashed border-gray-200 pt-1.5">
                <span>Feb 2026</span>
                <span className="text-gray-700 font-bold">$680 spent</span>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="hidden sm:block text-gray-200 text-2xl font-thin">|</div>

          {/* Merchant doodle */}
          <div className="flex flex-col gap-2">
            <div className="text-[10px] font-mono text-gray-400 text-center mb-1">top merchants</div>
            <div className="rotate-[-1.5deg] border-2 border-gray-800 rounded-sm p-3 w-52 sm:w-60 bg-white space-y-2"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              {[
                { name: 'Earls',       amt: '$210', pct: 85 },
                { name: 'Shoppers',    amt: '$145', pct: 58 },
                { name: 'Shell',       amt: '$110', pct: 44 },
                { name: 'Tim Hortons', amt: '$60',  pct: 24 },
              ].map(({ name, amt, pct }) => (
                <div key={name} className="flex items-center gap-2">
                  <div className="font-mono text-[10px] text-gray-700 w-20 shrink-0 truncate">{name}</div>
                  <div className="flex-1 h-3.5 bg-gray-100 border border-dashed border-gray-300 rounded-sm overflow-hidden">
                    <div className="h-full bg-primary/70 rounded-sm flex items-center pl-1" style={{ width: `${pct}%` }}>
                      {pct > 30 && <span className="font-mono text-[8px] text-gray-800 font-bold">{amt}</span>}
                    </div>
                  </div>
                  {pct <= 30 && <span className="font-mono text-[9px] text-gray-700 font-bold shrink-0">{amt}</span>}
                </div>
              ))}
              <div className="border-t border-dashed border-gray-200 pt-1.5 font-mono text-[10px] text-gray-400 flex justify-between">
                <span>Feb 2026</span><span className="text-gray-700 font-bold">4 merchants</span>
              </div>
            </div>
          </div>

          {/* Insights doodle */}
          <div className="flex flex-col gap-2">
            <div className="text-[10px] font-mono text-gray-400 text-center mb-1">track your budget</div>
            <div className="rotate-[1deg] border-2 border-gray-800 rounded-sm p-3 w-52 sm:w-60 bg-white space-y-3"
              style={{ boxShadow: '3px 3px 0 #1f2937' }}>
              {/* Budget bar */}
              <div>
                <div className="flex justify-between font-mono text-[10px] text-gray-500 mb-1">
                  <span>Mar budget</span><span>$525 / $700</span>
                </div>
                <div className="h-3 border-2 border-gray-800 rounded-sm overflow-hidden">
                  <div className="h-full bg-primary rounded-sm" style={{ width: '85%' }} />
                </div>
                <p className="font-mono text-[10px] text-gray-400 mt-1">75% used · 8 days left</p>
              </div>
              {/* Category bars */}
              <div className="space-y-1.5">
                {[
                  { label: 'Food', pct: 70, amt: '$210' },
                  { label: 'Health', pct: 48, amt: '$145' },
                  { label: 'Transport', pct: 37, amt: '$110' },
                ].map(({ label, pct, amt }) => (
                  <div key={label}>
                    <div className="flex justify-between font-mono text-[10px] text-gray-500 mb-0.5">
                      <span>{label}</span><span>{amt}</span>
                    </div>
                    <div className="h-2 bg-gray-100 border border-gray-300 rounded-sm overflow-hidden">
                      <div className="h-full bg-primary/60 rounded-sm" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-10 bg-gray-50">
        <div className="max-w-3xl mx-auto grid grid-cols-2 sm:grid-cols-3 gap-8">
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Spending trends</p>
              <p className="text-xs text-gray-400 mt-1">See where your money goes by category, merchant, and month.</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <FolderOpen className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Receipt management</p>
              <p className="text-xs text-gray-400 mt-1">All your receipts in one place. Search, filter, and export anytime.</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Export your data</p>
              <p className="text-xs text-gray-400 mt-1">Download your full history as a CSV anytime.</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Search className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Search instantly</p>
              <p className="text-xs text-gray-400 mt-1">Search any expense from your full history.</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <BarChart2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Budget tracking</p>
              <p className="text-xs text-gray-400 mt-1">See what you spend by category and store.</p>
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Savings Review</p>
              <p className="text-xs text-gray-400 mt-1">Find charges you forgot about with one tap.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Buylog */}
      <section className="px-6 py-10">
        <div className="max-w-2xl mx-auto grid sm:grid-cols-3 gap-6 text-center">
          <div>
            <p className="font-semibold text-gray-900">No manual entry</p>
            <p className="text-sm text-gray-400 mt-1">Snap a receipt or forward an email. Buylog handles the rest.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">No bank connection</p>
            <p className="text-sm text-gray-400 mt-1">We never ask for your bank details.</p>
          </div>
          <div>
            <p className="font-semibold text-gray-900">No subscription</p>
            <p className="text-sm text-gray-400 mt-1">Start for free with no credit card.</p>
          </div>
        </div>
      </section>

      {/* Social */}
      <section className="px-6 py-10 flex flex-col items-center text-center gap-3">
        <p className="text-sm font-semibold text-gray-900">Follow us for tips, updates and savings ideas</p>
        <div className="flex items-center gap-6">
          <a href="https://www.instagram.com/getbuylog/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#E1306C] transition-colors" aria-label="Buylog on Instagram">
            <FaInstagram size={40} />
          </a>
          <a href="https://www.tiktok.com/@getbuylog" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-black transition-colors" aria-label="Buylog on TikTok">
            <FaTiktok size={40} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-gray-100 px-6 py-6 flex items-center justify-between text-xs text-gray-400">
        <Logo />
        <div className="flex items-center gap-4">
          <Link to="/privacy" className="hover:text-gray-600">Privacy Policy</Link>
          <span>© {new Date().getFullYear()} Buylog</span>
        </div>
      </footer>

    </div>
  )
}
